import { UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RuleName } from '~/components/DesignSystem/Inputs/types';

type InputRulesType = Record<RuleName, UseControllerProps['rules']>;

export default function useInputRules({
  ruleName,
  required = false,
}: {
  ruleName?: RuleName;
  required?: boolean;
}) {
  const { t } = useTranslation('app');

  const inputRules: InputRulesType = {
    userEmail: {
      pattern: {
        value: new RegExp(/[^@]+[.]*[^@]*@[^@]+[.]+[^@]+/),
        message: t('invalidEmail'),
      },
      ...(required && {
        required: {
          value: true,
          message: t('requiredEmail'),
        },
      }),
    },
    userName: {
      minLength: {
        value: 3,
        message: t('invalidName'),
      },
      ...(required && {
        required: {
          value: true,
          message: t('requiredName'),
        },
      }),
    },
    userPhone: {
      minLength: {
        value: 7,
        message: t('invalidPhoneMinLength'),
      },
      pattern: {
        value: /^\d+$/,
        message: t('invalidPhoneNumber'),
      },
      ...(required && {
        required: {
          value: true,
          message: t('requiredPhone'),
        },
      }),
      validate: (phone) => {
        const isSequenceNumber = phone.indexOf('123456789') > -1;
        const isRepeatedChar = /^(\d)\1+$/.test(phone.number);
        const isSequencePhoneNumber = isSequenceNumber || isRepeatedChar;

        if (isSequencePhoneNumber) return t('sequencePhoneNumber');

        return true;
      },
    },
    radioList: {
      ...(required && {
        required: {
          value: true,
          message: t('selectValue'),
        },
      }),
    },
    reasonsList: {
      ...(required && {
        validate: (values) => {
          if (!Array.isArray(values) || values.length === 0)
            return t('requiredReason');

          return true;
        },
      }),
    },
    noteText: {
      ...(required && {
        required: {
          value: true,
          message: t('requiredNote'),
        },
      }),
    },
    rateComment: {
      ...(required && {
        required: {
          value: true,
          message: t('requiredComment'),
        },
      }),
    },
  };

  return ruleName ? inputRules[ruleName] : {};
}
